import { gsap } from 'gsap';

export default class MagneticCursor {
  constructor(options = {}) {
    this.options = {
      size: 30, // diamètre de l'anneau libre (px)
      padding: 5, // marge autour de l'élément accroché (px)
      pull: 0.4, // 0 à 1 : attraction de l'anneau vers le curseur
      splitStrength: 0.9, // sensibilité de la séparation RVB à la vitesse
      maxSplit: 14, // séparation max (px)
      fastThreshold: 20, // vitesse (px/frame) qui déclenche le déchirement
      magneticSelector: 'a, button, [data-magnetic]',
      ...options,
    };

    // Seulement pour les appareils avec une vraie souris
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches)
      return;

    this.reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)',
    ).matches;

    this.mouse = { x: window.innerWidth / 2, y: window.innerHeight / 2 };
    this.prev = { ...this.mouse };
    this.vel = { x: 0, y: 0 };
    this.target = null; // élément magnétique actuellement survolé
    this.lockRadius = 0;
    this.visible = false;

    this.tick = this.tick.bind(this);
    this.onMove = this.onMove.bind(this);
    this.onDown = this.onDown.bind(this);
    this.onUp = this.onUp.bind(this);
    this.onWindowLeave = this.onWindowLeave.bind(this);
    this.onWindowEnter = this.onWindowEnter.bind(this);

    this.build();
    this.bindEvents();
    gsap.ticker.add(this.tick);
  }

  build() {
    const { size } = this.options;
    const corners = [
      'borderTopLeftRadius',
      'borderTopRightRadius',
      'borderBottomRightRadius',
      'borderBottomLeftRadius',
    ];

    this.element = document.createElement('div');
    this.element.className = 'cursor';
    this.element.innerHTML = `
      <div class="cursor__layer cursor__layer--magenta"></div>
      <div class="cursor__layer cursor__layer--cyan"></div>
      <div class="cursor__layer cursor__layer--main"></div>
    `;

    this.dot = document.createElement('div');
    this.dot.className = 'cursor-dot';

    document.body.append(this.element, this.dot);
    document.documentElement.classList.add('has-custom-cursor');

    // x/y = centre de l'élément grâce au décalage de -50%
    gsap.set([this.element, this.dot], {
      xPercent: -50,
      yPercent: -50,
      opacity: 0,
    });
    gsap.set(this.element, {
      width: size,
      height: size,
      ...Object.fromEntries(corners.map((c) => [c, size / 2])),
    });

    // quickTo : tweens réutilisables, parfaits pour des valeurs mises à jour à chaque frame
    const opts = { duration: 0.35, ease: 'power3.out' };
    this.qx = gsap.quickTo(this.element, 'x', opts);
    this.qy = gsap.quickTo(this.element, 'y', opts);
    this.qw = gsap.quickTo(this.element, 'width', opts);
    this.qh = gsap.quickTo(this.element, 'height', opts);

    const qCorners = corners.map((c) => gsap.quickTo(this.element, c, opts));
    this.qr = (v) => qCorners.forEach((q) => q(v));

    // Le point suit le curseur sans aucun retard
    this.setDotX = gsap.quickSetter(this.dot, 'x', 'px');
    this.setDotY = gsap.quickSetter(this.dot, 'y', 'px');
  }

  bindEvents() {
    window.addEventListener('pointermove', this.onMove);
    window.addEventListener('pointerdown', this.onDown);
    window.addEventListener('pointerup', this.onUp);
    document.documentElement.addEventListener('mouseleave', this.onWindowLeave);
    document.documentElement.addEventListener('mouseenter', this.onWindowEnter);
  }

  /* ---------- Événements ---------- */

  onMove(e) {
    if (e.pointerType !== 'mouse') return;

    this.mouse.x = e.clientX;
    this.mouse.y = e.clientY;

    if (!this.visible) {
      // Première apparition : on place l'anneau directement, sans qu'il vole depuis le coin
      this.prev.x = e.clientX;
      this.prev.y = e.clientY;
      gsap.set(this.element, { x: e.clientX, y: e.clientY });
      this.show();
    }

    const t = e.target.closest?.(this.options.magneticSelector) || null;
    if (t !== this.target) this.setTarget(t);
  }

  onDown() {
    gsap.to(this.element, { scale: 0.85, duration: 0.2, ease: 'power2.out' });
  }

  onUp() {
    gsap.to(this.element, {
      scale: 1,
      duration: 0.4,
      ease: 'elastic.out(1, 0.5)',
    });
  }

  onWindowLeave() {
    this.hide();
  }

  onWindowEnter() {
    if (this.visible) return;
    this.show();
  }

  show() {
    this.visible = true;
    gsap.to([this.element, this.dot], {
      opacity: 1,
      duration: 0.3,
      overwrite: 'auto',
    });
  }

  hide() {
    this.visible = false;
    gsap.to([this.element, this.dot], {
      opacity: 0,
      duration: 0.3,
      overwrite: 'auto',
    });
  }

  /* ---------- Accrochage magnétique ---------- */

  setTarget(t) {
    this.target = t;
    this.element.classList.toggle('is-locked', !!t);
    gsap.to(this.dot, { scale: t ? 0 : 1, duration: 0.25, overwrite: 'auto' });

    if (t) {
      // On lit le rayon des coins une seule fois (getComputedStyle est coûteux)
      const r = t.getBoundingClientRect();
      const br = getComputedStyle(t).borderTopLeftRadius;
      const radius = br.includes('%')
        ? (Math.min(r.width, r.height) * parseFloat(br)) / 100
        : parseFloat(br) || 0;
      this.lockRadius = radius + this.options.padding;
    }
  }

  /* ---------- Boucle principale ---------- */

  tick(time, deltaTime) {
    if (!this.visible) return;

    const { size, padding, pull, splitStrength, maxSplit, fastThreshold } =
      this.options;
    const k = deltaTime / 16.67 || 1; // normalise la vitesse selon le framerate

    // Vitesse lissée du curseur (px par frame)
    const vx = (this.mouse.x - this.prev.x) / k;
    const vy = (this.mouse.y - this.prev.y) / k;
    this.vel.x += (vx - this.vel.x) * 0.2;
    this.vel.y += (vy - this.vel.y) * 0.2;
    this.prev.x = this.mouse.x;
    this.prev.y = this.mouse.y;

    this.setDotX(this.mouse.x);
    this.setDotY(this.mouse.y);

    // Rectangle de l'élément accroché (relu à chaque frame : le carrousel bouge)
    let rect = null;
    if (this.target) {
      rect = this.target.getBoundingClientRect();
      const m = padding * 2;
      const outside =
        !this.target.isConnected ||
        this.mouse.x < rect.left - m ||
        this.mouse.x > rect.right + m ||
        this.mouse.y < rect.top - m ||
        this.mouse.y > rect.bottom + m;
      if (outside) {
        this.setTarget(null);
        rect = null;
      }
    }

    if (rect) {
      // Mode magnétique : l'anneau épouse l'élément, légèrement tiré vers le curseur
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      this.qx(cx + (this.mouse.x - cx) * pull);
      this.qy(cy + (this.mouse.y - cy) * pull);
      this.qw(rect.width + padding * 2);
      this.qh(rect.height + padding * 2);
      this.qr(this.lockRadius);
    } else {
      // Mode libre : petit anneau rond centré sur le curseur
      this.qx(this.mouse.x);
      this.qy(this.mouse.y);
      this.qw(size);
      this.qh(size);
      this.qr(size / 2);
    }

    // Séparation RVB proportionnelle à la vitesse (réduite quand l'anneau est accroché)
    if (!this.reduceMotion) {
      const f = this.target ? 0.4 : 1;
      const clamp = gsap.utils.clamp(-maxSplit, maxSplit);
      const sx = clamp(this.vel.x * splitStrength * f);
      const sy = clamp(this.vel.y * splitStrength * f);
      this.element.style.setProperty('--sx', `${sx.toFixed(2)}px`);
      this.element.style.setProperty('--sy', `${sy.toFixed(2)}px`);

      const speed = Math.hypot(this.vel.x, this.vel.y);
      this.element.classList.toggle(
        'is-fast',
        speed > fastThreshold && !this.target,
      );
    }
  }

  destroy() {
    gsap.ticker.remove(this.tick);
    window.removeEventListener('pointermove', this.onMove);
    window.removeEventListener('pointerdown', this.onDown);
    window.removeEventListener('pointerup', this.onUp);
    document.documentElement.removeEventListener(
      'mouseleave',
      this.onWindowLeave,
    );
    document.documentElement.removeEventListener(
      'mouseenter',
      this.onWindowEnter,
    );
    gsap.killTweensOf([this.element, this.dot]);
    this.element?.remove();
    this.dot?.remove();
    document.documentElement.classList.remove('has-custom-cursor');
  }
}

const cursor = new MagneticCursor();
